import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { Star } from "@phosphor-icons/react";
import RevealText from "@/components/site/RevealText";
import BookingWidget from "@/components/site/BookingWidget";
import { BookDirectSave } from "@/components/site/BookDirectSave";
import { HostBadges } from "@/components/site/HostBadges";
import { BookingScores } from "@/components/site/BookingScores";
import PropertyCard from "@/components/site/PropertyCard";
import Marquee from "@/components/site/Marquee";
import EventsTeaser from "@/components/site/EventsTeaser";
import { HomePetOfTheWeek } from "@/components/site/HomePetOfTheWeek";
import { properties } from "@/data/properties";
import { reviews } from "@/data/reviews";
import { hosts } from "@/data/about";
import Seo, { SITE } from "@/components/site/Seo";
import AnswerBlocks from "@/components/site/AnswerBlocks";
import hero640 from "@/assets/hero/sunset-640.webp";
import hero960 from "@/assets/hero/sunset-960.webp";
import hero1280 from "@/assets/hero/sunset-1280.webp";
import hero1600 from "@/assets/hero/sunset-1600.webp";
import hero1920 from "@/assets/hero/sunset-1920.webp";

// Unsplash images: auto=format serves WebP/AVIF, responsive srcset trims mobile payload.
const unsplash = (id, w, q = 60) =>
  `https://images.unsplash.com/${id}?crop=entropy&fit=crop&auto=format&q=${q}&w=${w}`;
const srcset = (id, widths, q = 60) => widths.map((w) => `${unsplash(id, w, q)} ${w}w`).join(", ");
const ATMOS_ID = "photo-1691423332423-f2132628ff72";
const HERO = hero1920;
const HERO_SRCSET = [
  `${hero640} 640w`,
  `${hero960} 960w`,
  `${hero1280} 1280w`,
  `${hero1600} 1600w`,
  `${hero1920} 1920w`,
].join(", ");
const ATMOS = unsplash(ATMOS_ID, 1200);
const ATMOS_SRCSET = srcset(ATMOS_ID, [640, 960, 1200]);

const chapters = [
  { n: "01", title: "Our Story", body: "Double Rainbow Suites began with a simple belief: that a short stay can feel like a private world of its own. We restore and design each home ourselves — funky, warm and considered down to the last detail." },
  { n: "02", title: "Our Vibe", body: "Steps from Wilton Drive and a short drive from the beach, our suites sit in one of Florida's most welcoming, vibrant neighborhoods. Inclusive by nature, elevated by design." },
  { n: "03", title: "Our Promise", body: "King beds, blackout curtains, fast Wi-Fi and hosts who answer in minutes. Whether it's a weekend escape or a slow month in the sun, everything is ready for you." },
];

export default function Home() {
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "28%"]);
  const scale = useTransform(scrollYProgress, [0, 1], [1, 1.15]);
  const overlayOpacity = useTransform(scrollYProgress, [0, 1], [0.35, 0.7]);

  return (
    <main>
      <Seo
        title="Double Rainbow Suites | Short-Term & Vacation Rentals in Wilton Manors & Fort Lauderdale"
        description="Double Rainbow Suites is the premier short-term & vacation rental company in Wilton Manors and Fort Lauderdale, Florida — pet-friendly boutique king suites and private-pool homes with instant booking and 5-star reviews, plus co-hosting & property management support for local owners."
        canonical={`${SITE}/`}
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "WebPage",
          "@id": `${SITE}/#webpage`,
          url: `${SITE}/`,
          name: "Double Rainbow Suites — Wilton Manors & Fort Lauderdale Vacation Rentals",
          about: { "@id": `${SITE}/#business` },
          primaryImageOfPage: `${SITE}${HERO}`,
          description:
            "The premier short-term and vacation rental company in Wilton Manors and Fort Lauderdale, FL, offering boutique king suites, private-pool homes and co-hosting / vacation-rental management support for owners.",
        }}
      />
      {/* HERO */}
      <section ref={heroRef} data-testid="hero-section" className="relative min-h-[100svh] overflow-hidden">
        <motion.div style={{ y, scale }} className="absolute inset-0">
          <img src={HERO} srcSet={HERO_SRCSET} sizes="100vw" alt="Fort Lauderdale beach boardwalk at golden-hour sunset framed by palm trees" fetchPriority="high" decoding="async" className="w-full h-full object-cover" />
        </motion.div>
        <motion.div style={{ opacity: overlayOpacity }} className="absolute inset-0 bg-emerald" />
        <div className="absolute inset-0 bg-gradient-to-t from-emerald/80 via-transparent to-emerald/30" />

        <a
          href="https://serenitydiveco.com/"
          target="_blank"
          rel="noopener noreferrer"
          data-testid="hero-photo-credit"
          aria-label="Hero photo courtesy of Serenity Dive Co (opens in a new tab)"
          className="absolute top-28 right-4 md:top-32 md:right-6 z-20 text-[10px] md:text-xs tracking-wide text-sand/50 hover:text-gold/90 transition-colors duration-300 [text-shadow:0_1px_3px_rgba(0,0,0,0.35)]"
        >
          Photo: Serenity Dive Co
        </a>

        <div className="relative z-10 min-h-[100svh] max-w-[1400px] mx-auto px-6 md:px-10 flex flex-col justify-end pt-28 md:pt-32 pb-24 md:pb-24">
          <p className="overline text-gold mb-4 md:mb-6 hero-rise" style={{ animationDelay: "0.05s" }}>
            Wilton Manors · Fort Lauderdale · Florida
          </p>
          <h1 className="font-display font-light text-sand text-[12vw] md:text-[9vw] leading-[0.86] tracking-tight">
            <RevealText lines={["Where every", "stay ends in", "a rainbow."]} delay={0.12} stagger={0.08} />
          </h1>
          <p
            className="text-sand/85 text-base md:text-xl max-w-xl mt-5 md:mt-8 font-light hero-rise"
            style={{ animationDelay: "0.3s" }}
          >
            Double Rainbow Suites offers boutique vacation rentals in Wilton Manors and Fort Lauderdale, all units are pet-friendly king suites and private-pool option homes near Wilton Drive, Fort Lauderdale Beach and South Florida's best nightlife — and minutes from Port Everglades.
          </p>
          <p
            className="flex items-center gap-2.5 mt-4 md:mt-5 text-sand text-sm md:text-base font-medium tracking-wide hero-rise"
            style={{ animationDelay: "0.4s" }}
            data-testid="hero-lgbtq-line"
          >
            <span className="flex gap-[3px]" aria-hidden="true">
              {["#E07A5F", "#F2CC8F", "#81B29A"].map((c) => (
                <span key={c} className="w-1 h-4 rounded-full" style={{ background: c }} />
              ))}
            </span>
            LGBTQ+ owned &amp; operated — all are welcome
          </p>
          <div
            data-testid="hero-trust-strip"
            className="mt-4 md:mt-6 inline-flex items-center gap-3 self-start rounded-full border border-sand/20 bg-emerald/25 backdrop-blur-md px-4 md:px-5 py-2 md:py-2.5 text-sand hero-rise"
            style={{ animationDelay: "0.5s" }}
          >
            <span className="flex items-center gap-1.5 font-medium text-sm md:text-base">
              <Star size={16} weight="fill" className="text-gold" /> Guest favorite
            </span>
            <span className="w-px h-4 bg-sand/25" aria-hidden="true" />
            <span className="text-sm md:text-base font-semibold tabular-nums">4.9</span>
            <span className="flex gap-0.5" aria-hidden="true">
              {[0, 1, 2, 3, 4].map((i) => (
                <Star key={i} size={13} weight="fill" className="text-gold" />
              ))}
            </span>
          </div>
          <div
            className="flex flex-wrap gap-4 mt-7 md:mt-10 hero-rise"
            style={{ animationDelay: "0.55s" }}
          >
            <Link to="/properties" data-testid="hero-explore-btn" className="group inline-flex items-center gap-2 rounded-full bg-sunset px-8 py-4 text-sand font-medium transition-colors duration-300 hover:bg-sand hover:text-emerald">
              Explore stays <span className="transition-transform duration-300 group-hover:translate-x-1">→</span>
            </Link>
            <Link to="/contact" data-testid="hero-contact-btn" className="inline-flex items-center gap-2 rounded-full border border-sand/40 px-8 py-4 text-sand transition-colors duration-300 hover:bg-sand/10">
              Get in touch
            </Link>
          </div>
        </div>
      </section>

      {/* BOOKING WIDGET */}
      <section className="relative z-20 max-w-[1400px] mx-auto px-6 md:px-10 -mt-14 md:-mt-16">
        <BookingWidget />
        <div className="mt-4">
          <BookDirectSave compact />
        </div>
        <HostBadges className="mt-8" />
      </section>

      {/* PROPERTIES */}
      <section data-testid="properties-section" className="max-w-[1400px] mx-auto px-6 md:px-10 py-24 md:py-32">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-14">
          <div>
            <p className="overline text-sunset mb-4">The collection</p>
            <h2 className="font-display text-4xl md:text-6xl leading-[0.95] tracking-tight max-w-xl">
              Four homes. One unforgettable neighborhood.
            </h2>
          </div>
          <Link to="/properties" data-testid="view-all-properties" className="group inline-flex items-center gap-2 text-emerald hover:text-sunset transition-colors duration-300 whitespace-nowrap">
            View all stays <span className="transition-transform duration-300 group-hover:translate-x-1">→</span>
          </Link>
        </div>

        <div className="grid md:grid-cols-2 gap-5 md:gap-6">
          {properties.map((p, i) => (
            <PropertyCard key={p.slug} property={p} index={i} wide={p.featured} />
          ))}
        </div>
      </section>

      {/* MANIFESTO */}
      <section data-testid="manifesto-section" className="bg-emerald text-sand py-24 md:py-40">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10">
          <div className="grid lg:grid-cols-2 gap-16 items-start">
            <div className="lg:sticky lg:top-28">
              <p className="overline text-gold mb-6">Why Double Rainbow</p>
              <h2 className="font-display text-4xl md:text-6xl leading-[0.95] tracking-tight mb-8">
                A short-term rental company that actually sweats the details.
              </h2>
              <div className="relative overflow-hidden rounded-2xl aspect-[4/3] mt-8">
                <img src={ATMOS} srcSet={ATMOS_SRCSET} sizes="(min-width: 768px) 50vw, 100vw" alt="Poolside at golden hour" loading="lazy" decoding="async" className="w-full h-full object-cover" />
              </div>
            </div>
            <div>
              {chapters.map((c, i) => (
                <motion.div
                  key={c.n}
                  initial={{ opacity: 0, y: 40 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-100px" }}
                  transition={{ duration: 0.8, delay: i * 0.05, ease: [0.16, 1, 0.3, 1] }}
                  className="py-10 border-b border-sand/15 flex gap-6 md:gap-10"
                >
                  <span className="font-display text-6xl md:text-7xl text-sunset/90 leading-none">{c.n}</span>
                  <div>
                    <h3 className="font-display text-3xl mb-3">{c.title}</h3>
                    <p className="text-sand/75 font-light leading-relaxed max-w-md">{c.body}</p>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <Marquee />

      {/* REVIEWS */}
      <section data-testid="reviews-section" className="max-w-[1400px] mx-auto px-6 md:px-10 py-24 md:py-32">
        <div className="mb-14">
          <p className="overline text-sunset mb-4">Guest words</p>
          <h2 className="font-display text-4xl md:text-6xl leading-[0.95] tracking-tight max-w-2xl">
            Loved by travelers from around the world.
          </h2>
        </div>
        <BookingScores className="mb-14" />
        <div className="columns-1 md:columns-2 lg:columns-3 gap-6 [column-fill:_balance]">
          {reviews.map((r, i) => (
            <motion.figure
              key={i}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.7, delay: (i % 3) * 0.08 }}
              className="break-inside-avoid mb-6 border border-emerald/10 rounded-2xl p-7 bg-sand"
            >
              <div className="text-sunset text-lg mb-4">★★★★★</div>
              <blockquote className="font-display text-xl md:text-2xl leading-snug text-emerald mb-6">"{r.text}"</blockquote>
              <figcaption className="text-sm text-emerald-soft">
                <span className="font-medium text-emerald">{r.name}</span> · {r.date}
              </figcaption>
            </motion.figure>
          ))}
        </div>
      </section>

      {/* PET OF THE WEEK */}
      <HomePetOfTheWeek />

      {/* MEET YOUR HOSTS TEASER */}
      <section className="max-w-[1400px] mx-auto px-6 md:px-10 pb-4 md:pb-8">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
          data-testid="home-hosts-teaser"
          className="flex flex-col sm:flex-row items-center gap-6 md:gap-9 rounded-3xl bg-emerald text-sand p-8 md:p-12"
        >
          <img
            src={hosts.photo}
            alt={`${hosts.names} — hosts of Double Rainbow Suites`}
            width={226}
            height={195}
            loading="lazy"
            decoding="async"
            className="w-32 h-32 md:w-40 md:h-40 shrink-0 object-contain [filter:drop-shadow(0_10px_20px_rgba(0,0,0,0.25))]"
          />
          <div className="text-center sm:text-left">
            <p className="overline text-gold mb-3">Meet your hosts</p>
            <p className="font-display text-2xl md:text-3xl leading-snug max-w-xl">
              Hosted personally by {hosts.names} — Wilton Manors locals, short-term-rental investors and Airbnb Superhosts (three years running) who design every home themselves.
            </p>
            <Link
              to="/about"
              data-testid="home-hosts-link"
              className="group inline-flex items-center gap-2 mt-5 text-sand font-medium hover:text-gold transition-colors duration-300"
            >
              Read our story <span className="transition-transform duration-300 group-hover:translate-x-1">→</span>
            </Link>
          </div>
        </motion.div>
      </section>

      {/* CRUISE STRIP — pre/post-cruise stays */}
      <section className="max-w-[1400px] mx-auto px-6 md:px-10" data-testid="home-cruise-strip">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.8 }}
          className="border-y border-emerald/12 py-12 md:py-16 grid md:grid-cols-[1.4fr_1fr] gap-10 items-center"
        >
          <div>
            <p className="overline text-sunset mb-4">Sailing from Port Everglades?</p>
            <h2 className="font-display text-3xl md:text-4xl leading-tight tracking-tight text-emerald mb-4">
              A pre-cruise stay that feels like the vacation started early.
            </h2>
            <p className="text-emerald-soft font-light leading-relaxed max-w-xl">
              Our pet-friendly vacation rentals are about 15 minutes from the Port Everglades terminals — relaxed cruise-port accommodation in Fort Lauderdale for the night before you sail or the days after. Stay near Port Everglades with a full kitchen, free parking and the beach 10 minutes away.
            </p>
          </div>
          <div className="flex flex-col gap-5 md:items-end">
            <Link to="/fort-lauderdale-vacation-rentals-near-cruise-port" data-testid="home-cruise-link-rentals" className="group inline-flex items-center gap-2 text-emerald font-medium hover:text-sunset transition-colors duration-300">
              Vacation rentals near the cruise port <span className="transition-transform duration-300 group-hover:translate-x-1">→</span>
            </Link>
            <Link to="/fort-lauderdale-cruise-port-hotels" data-testid="home-cruise-link-before-after" className="group inline-flex items-center gap-2 text-emerald font-medium hover:text-sunset transition-colors duration-300">
              Before or after your cruise <span className="transition-transform duration-300 group-hover:translate-x-1">→</span>
            </Link>
          </div>
        </motion.div>
      </section>

      {/* AUTHORITY ANSWERS */}
      <AnswerBlocks />

      {/* WHAT'S ON */}
      <EventsTeaser theme="light" />

      {/* CTA */}
      <section className="max-w-[1400px] mx-auto px-6 md:px-10 pb-24 md:pb-32">
        <div className="relative overflow-hidden rounded-3xl bg-sand-2 border border-emerald/10 p-10 md:p-20 text-center">
          <p className="overline text-sunset mb-5">Ready when you are</p>
          <h2 className="font-display text-4xl md:text-7xl leading-[0.95] tracking-tight mb-8 max-w-3xl mx-auto">
            Your rainbow is waiting in South Florida.
          </h2>
          <Link to="/properties" data-testid="cta-book-btn" className="group inline-flex items-center gap-2 rounded-full bg-sunset px-8 py-4 text-sand font-medium transition-colors duration-300 hover:bg-emerald">
            Book your stay <span className="transition-transform duration-300 group-hover:translate-x-1">→</span>
          </Link>
        </div>
      </section>
    </main>
  );
}
